import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect } from 'react';
import Select, { components } from 'react-select';
import { Label } from 'reactstrap';

const CheckboxOption = (props) => (
  <components.Option {...props}>
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        style={{ marginRight: 8, cursor: 'pointer' }}
        aria-label={`Select ${props.label}`}
      />
      <Label style={{ cursor: 'pointer', margin: 0, flex: 1 }}>
        {props.label}
      </Label>
    </div>
  </components.Option>
);

CheckboxOption.propTypes = {
  isSelected: PropTypes.bool,
  label: PropTypes.string.isRequired,
  data: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    isDisabled: PropTypes.bool,
  }),
  innerProps: PropTypes.shape({}),
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

CheckboxOption.defaultProps = {
  isSelected: false,
  data: null,
  innerProps: {},
  innerRef: null,
};

const ValueContainer = ({ children, ...props }) => {
  const { getValue, hasValue } = props;
  const selectedValues = getValue();

  const { showTags, showCount, formatPlaceholder, customDisplayText } = props.selectProps;

  if (!hasValue || selectedValues.length === 0) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  if (showTags) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  let displayText = '';

  if (customDisplayText) {
    displayText = typeof customDisplayText === 'function'
      ? customDisplayText(selectedValues)
      : customDisplayText;
  }

  else if (formatPlaceholder) {
    displayText = formatPlaceholder(selectedValues);
  }

  else if (showCount) {
    displayText = `${selectedValues.length} seleccionado${selectedValues.length !== 1 ? 's' : ''}`;
  }

  else {
    const labels = selectedValues.map(item => item.label).join(', ');
    displayText = labels.length > 50 ? `${labels.substring(0, 50)}...` : labels;
  }

  return (
    <components.ValueContainer {...props}>
      <div style={{
        position: 'absolute',
        left: '8px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#333',
        fontSize: '14px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 'calc(100% - 60px)',
        pointerEvents: 'none'
      }}>
        {displayText}
      </div>
      {children}
    </components.ValueContainer>
  );
};

ValueContainer.propTypes = {
  children: PropTypes.node,
  getValue: PropTypes.func,
  hasValue: PropTypes.bool,
  selectProps: PropTypes.shape({
    showTags: PropTypes.bool,
    showCount: PropTypes.bool,
    formatPlaceholder: PropTypes.func,
    customDisplayText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  }),
};

ValueContainer.defaultProps = {
  children: null,
  getValue: () => [],
  hasValue: false,
  selectProps: {},
};

const MultiValueLabel = (props) => (
  <components.MultiValueLabel {...props}>
    <span style={{ fontSize: '0.875rem' }}>{props.data.label}</span>
  </components.MultiValueLabel>
);

MultiValueLabel.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
  }),
};

MultiValueLabel.defaultProps = {
  data: { label: '' },
};

const getCustomStyles = (maxHeight = '38px', minWidth = '200px') => ({
  control: (provided, state) => ({
    ...provided,
    minHeight: maxHeight,
    minWidth,
    borderColor: state.isFocused ? '#2684FF' : '#cccccc',
    boxShadow: state.isFocused ? '0 0 0 1px #2684FF' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#2684FF' : '#b3b3b3',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px',
    flexWrap: 'wrap',
    overflow: 'hidden',
    maxHeight: '100px',
    overflowY: 'auto',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#e0e0e0',
    borderRadius: '3px',
    margin: '2px',
    maxWidth: '150px',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    padding: '2px 6px',
    fontSize: '0.875rem',
    color: '#333',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#d32f2f',
      color: 'white',
    },
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    alignSelf: 'flex-start',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: '4px',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '4px',
  }),
  menu: (provided) => ({
    ...provided,
    minWidth: '250px',
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '300px',
  }),
  option: (provided, state) => {
    let bgColor = 'white';
    if (state.isSelected) {
      bgColor = '#deebff';
    } else if (state.isFocused) {
      bgColor = '#f5f5f5';
    }

    return ({
      ...provided,
      backgroundColor: bgColor,
      color: '#333',
      cursor: 'pointer',
      padding: '8px 12px',
      '&:active': {
        backgroundColor: '#deebff',
      },
    })
  },
  placeholder: (provided) => ({
    ...provided,
    color: '#999',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
});

const MultiSelectCheck = ({
  options,
  defaultValue,
  value: controlledValue,
  onChange,
  placeholder,
  isClearable,
  isSearchable,
  isDisabled,
  isLoading,
  maxHeight,
  minWidth,
  closeMenuOnSelect,
  noOptionsMessage,
  loadingMessage,
  customStyles,
  formatPlaceholder,
  showCount,
  showTags,
  customDisplayText,
  className,
  id,
  name,
  menuPlacement,
  menuPosition,
}) => {

  const [internalValue, setInternalValue] = useState(defaultValue || []);

  const selectedValue = controlledValue !== undefined ? controlledValue : internalValue;

  useEffect(() => {
    if (controlledValue !== undefined) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);

  const handleChange = (newValue) => {
    const valueToSet = newValue || [];

    if (controlledValue === undefined) {
      setInternalValue(valueToSet);
    }

    if (onChange) {
      onChange(valueToSet);
    }
  };

  const mergedStyles = useMemo(() => {
    const baseStyles = getCustomStyles(maxHeight, minWidth);

    if (!showTags) {
      baseStyles.multiValue = (provided) => ({
        ...provided,
        display: 'none',
      });
      baseStyles.valueContainer = (provided) => ({
        ...provided,
        padding: '2px 8px',
        flexWrap: 'nowrap',
        position: 'relative',
      });
      baseStyles.input = (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
        opacity: 0,
        width: 0,
      });
    }

    if (!customStyles || Object.keys(customStyles).length === 0) {
      return baseStyles;
    }

    const merged = {};
    const allKeys = new Set([
      ...Object.keys(baseStyles),
      ...Object.keys(customStyles)
    ]);

    allKeys.forEach(key => {
      if (baseStyles[key] && customStyles[key]) {
        merged[key] = (provided, state) => ({
          ...baseStyles[key](provided, state),
          ...customStyles[key](provided, state),
        });
      } else {
        merged[key] = customStyles[key] || baseStyles[key];
      }
    });

    return merged;
  }, [maxHeight, minWidth, customStyles, showTags]);

  return (
    <Select
      id={id}
      name={name}
      className={className}
      classNamePrefix="multi-select"
      isMulti
      options={options}
      value={selectedValue}
      onChange={handleChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      closeMenuOnSelect={closeMenuOnSelect}
      hideSelectedOptions={false}
      showTags={showTags}
      showCount={showCount}
      formatPlaceholder={formatPlaceholder}
      customDisplayText={customDisplayText}
      components={{
        Option: CheckboxOption,
        ValueContainer,
        MultiValueLabel,
      }}
      styles={mergedStyles}
      noOptionsMessage={() => noOptionsMessage}
      loadingMessage={() => loadingMessage}
      menuPlacement={menuPlacement}
      menuPosition={menuPosition}
    />
  );
};

MultiSelectCheck.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      isDisabled: PropTypes.bool,
    })
  ).isRequired,
  defaultValue: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  value: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  maxHeight: PropTypes.string,
  minWidth: PropTypes.string,
  closeMenuOnSelect: PropTypes.bool,
  noOptionsMessage: PropTypes.string,
  loadingMessage: PropTypes.string,
  customStyles: PropTypes.shape({
    control: PropTypes.func,
    valueContainer: PropTypes.func,
    multiValue: PropTypes.func,
    input: PropTypes.func,
    indicatorsContainer: PropTypes.func,
    clearIndicator: PropTypes.func,
    dropdownIndicator: PropTypes.func,
    menu: PropTypes.func,
    menuList: PropTypes.func,
    option: PropTypes.func,
    placeholder: PropTypes.func,
  }),
  formatPlaceholder: PropTypes.func,
  showCount: PropTypes.bool,
  showTags: PropTypes.bool,
  customDisplayText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
  menuPosition: PropTypes.oneOf(['fixed', 'absolute']),
};

MultiSelectCheck.defaultProps = {
  defaultValue: [],
  value: undefined,
  onChange: null,
  placeholder: 'Seleccione...',
  isClearable: true,
  isSearchable: true,
  isDisabled: false,
  isLoading: false,
  maxHeight: '38px',
  minWidth: '200px',
  closeMenuOnSelect: false,
  noOptionsMessage: 'No hay opciones',
  loadingMessage: 'Cargando...',
  customStyles: {},
  formatPlaceholder: null,
  showCount: true,
  showTags: true,
  customDisplayText: null,
  className: '',
  id: undefined,
  name: undefined,
  menuPlacement: 'auto',
  menuPosition: 'absolute',
};

export default MultiSelectCheck;